import React, { useState, useEffect, useCallback } from 'react';

const CATEGORIES = [
  { key: 'all', label: 'All Photos' },
  { key: 'plant', label: 'Plant & Facility' },
  { key: 'products', label: 'Products' },
  { key: 'team', label: 'Our Team' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'events', label: 'Events' },
];

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index of open image

  useEffect(() => {
    fetch('/api/gallery')
      .then(r => r.json())
      .then(data => { setImages(data); setFiltered(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') setFiltered(images);
    else setFiltered(images.filter(img => img.category === activeCategory));
  }, [activeCategory, images]);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prevImage = useCallback(() => setLightbox(i => (i - 1 + filtered.length) % filtered.length), [filtered.length]);
  const nextImage = useCallback(() => setLightbox(i => (i + 1) % filtered.length), [filtered.length]);

  useEffect(() => {
    const onKey = e => {
      if (lightbox === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prevImage, nextImage]);

  const catLabel = key => CATEGORIES.find(c => c.key === key)?.label || key;

  return (
    <div style={{ background: '#fff' }}>

      {/* Hero */}
      <section className="page-hero">
        <div className="page-container">
          <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: '9999px', padding: '0.3rem 1rem', fontSize: '0.8rem', fontWeight: 700, marginBottom: '1rem' }}>
            &#x1F4F7; Gallery
          </div>
          <h1>Our Gallery</h1>
          <p>A glimpse into our plant, products, team and daily operations.</p>
        </div>
      </section>

      {/* Gallery */}
      <section className="section" style={{ background: 'var(--brand-sky)' }}>
        <div className="page-container">

          {/* Category Filters */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2.5rem' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                onClick={() => setActiveCategory(c.key)}
                style={{
                  padding: '0.45rem 1.1rem',
                  borderRadius: '9999px',
                  border: activeCategory === c.key ? 'none' : '1.5px solid var(--border-light)',
                  background: activeCategory === c.key ? 'var(--brand)' : 'white',
                  color: activeCategory === c.key ? 'white' : 'var(--text-body)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {c.label}
                {c.key !== 'all' && (
                  <span style={{ marginLeft: '0.4rem', background: activeCategory === c.key ? 'rgba(255,255,255,0.25)' : 'var(--brand-light)', color: activeCategory === c.key ? 'white' : 'var(--brand)', borderRadius: '9999px', padding: '0.05rem 0.45rem', fontSize: '0.72rem', fontWeight: 700 }}>
                    {images.filter(i => i.category === c.key).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Count */}
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            {loading ? 'Loading...' : `${filtered.length} photo${filtered.length !== 1 ? 's' : ''}`}
          </p>

          {/* Grid */}
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 1rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#x1F4F7;</div>
              <p style={{ fontWeight: 600, fontSize: '1.05rem', color: 'var(--text-dark)', marginBottom: '0.5rem' }}>No photos yet</p>
              <p>Check back soon — we're adding new photos regularly.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {filtered.map((img, idx) => (
                <div
                  key={img.id}
                  onClick={() => openLightbox(idx)}
                  style={{ borderRadius: '14px', overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', transition: 'transform 0.25s, box-shadow 0.25s', aspectRatio: '4/3' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(29,78,216,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; }}
                >
                  <img
                    src={img.image_path}
                    alt={img.title || img.category}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {/* Hover overlay */}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(12,26,46,0.75) 0%, transparent 50%)', opacity: 0, transition: 'opacity 0.25s', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '1rem' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0'; }}
                  >
                    {img.title && <p style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.25rem' }}>{img.title}</p>}
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '9999px', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, display: 'inline-block', width: 'fit-content' }}>
                      {catLabel(img.category)}
                    </span>
                  </div>
                  {/* Zoom icon */}
                  <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    &#x1F50D;
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && filtered[lightbox] && (
        <div
          onClick={closeLightbox}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          {/* Close */}
          <button onClick={closeLightbox} style={{ position: 'absolute', top: '1rem', right: '1.25rem', background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: '1.5rem', width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#x2715;</button>

          {/* Prev */}
          <button onClick={e => { e.stopPropagation(); prevImage(); }} style={{ position: 'absolute', left: '1rem', background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: '1.5rem', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#8592;</button>

          {/* Image */}
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src={filtered[lightbox].image_path}
              alt={filtered[lightbox].title || ''}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '10px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            />
            <div style={{ textAlign: 'center' }}>
              {filtered[lightbox].title && <p style={{ color: 'white', fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>{filtered[lightbox].title}</p>}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center' }}>
                <span style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)', borderRadius: '9999px', padding: '0.2rem 0.75rem', fontSize: '0.78rem', fontWeight: 600 }}>
                  {catLabel(filtered[lightbox].category)}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{lightbox + 1} / {filtered.length}</span>
              </div>
            </div>
          </div>

          {/* Next */}
          <button onClick={e => { e.stopPropagation(); nextImage(); }} style={{ position: 'absolute', right: '1rem', background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: '1.5rem', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#8594;</button>
        </div>
      )}
    </div>
  );
}
